import { differenceInDays, startOfDay } from "date-fns";
import { memo } from "react";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { useFormatDate } from "./DateDisplayDate.utilities";

// Mirror the sibling DateDisplayDateOrRecentLabel below — render the friendly
// placeholder ("--") when value is missing. The previous implementation threw,
// which crashed the surrounding render via React's ErrorBoundary even though
// callers reach this path with partial mock data or sparse API responses
// (e.g. ScheduleHistory shifts whose date fields are optional in the entity).
// The TypeScript prop type still requires Date, so the throw was only ever a
// runtime backstop — production users now see "--" instead of a crashed tree.

export interface IDateDisplayDateProps {
    /** Date to display */
    value: Date;

    /** Shows the date (Month, Day, Year)
     * @default true
     */
    showDate?: boolean;

    /** Shows the time, will appear after date
     * @default false
     */
    showTime?: boolean;

    /** Shows the day of week (Mon, Tue, Wed...) at the beginning of the date
     * @default false
     */
    showDayOfWeek?: boolean;

    "data-testid": string;

    /** Use ONLY when static dates cannot be used at Storybook story level.
     *  Snapshots for the component will be ignored in Chromatic, if included.
     */
    "data-chromatic"?: "ignore";

    className?: string;

    /** Show the current year if the date is in the current year
     * @default false
     */
    hideCurrentYear?: boolean;

    /**
     * Add an optional aria-label for improved accessibility (screen readers, etc).
     * Aria-labels must be applied directly to the HTML element.
     */
    ariaLabel?: string;
}

/**
 * Displays a formatted date as text, use whenever you want to show date for display only
 */
export const DateDisplayDate = memo(function DateDisplayDateInternal({
    value,
    className,
    "data-testid": dataTestId,
    "data-chromatic": dataChromatic,
    showDate = true,
    showTime = false,
    showDayOfWeek = false,
    hideCurrentYear = false,
    ariaLabel,
}: IDateDisplayDateProps) {
    const formatDate = useFormatDate();

    if (value === null || value === undefined) {
        return <>{friendlyDefaultDisplayValue}</>;
    }

    return (
        <span
            style={{ whiteSpace: "nowrap" }}
            data-testid={dataTestId}
            data-chromatic={dataChromatic}
            className={className}
            aria-label={ariaLabel}
        >
            {formatDate(value, {
                showDate,
                showTime,
                showDayOfWeek,
                hideCurrentYear,
            })}
        </span>
    );
});

interface IDateDisplayDateOrRecentLabelOptions {
    value?: Date | null;
    showTime: boolean;
    showDayOfWeek: boolean;
    hideCurrentYear?: boolean;
    "data-testid": string;
}

/**
 * Displays the date as a formatted string along with text for recent dates (Today, Tomorrow, Yesterday)
 */
export function DateDisplayDateOrRecentLabel({
    value,
    "data-testid": dataTestId,
    showTime = true,
    showDayOfWeek = false,
    hideCurrentYear = false,
}: IDateDisplayDateOrRecentLabelOptions) {
    if (!value) {
        return <>{friendlyDefaultDisplayValue}</>;
    }
    const diffInDays = differenceInDays(startOfDay(value), startOfDay(new Date()));

    if (-1 <= diffInDays && diffInDays <= 1) {
        let relativeVerbiage = "";
        if (diffInDays === 0) {
            relativeVerbiage = "Today";
        } else if (diffInDays === -1) {
            relativeVerbiage = "Yesterday";
        } else if (diffInDays === 1) {
            relativeVerbiage = "Tomorrow";
        }
        const timeDisplay = showTime ? (
            <>
                " - "
                <DateDisplayDate
                    showDate={false}
                    showTime
                    value={value}
                    data-testid={dataTestId}
                    showDayOfWeek={showDayOfWeek}
                    hideCurrentYear={hideCurrentYear}
                />
            </>
        ) : null;
        return (
            <>
                {relativeVerbiage}
                {timeDisplay}
            </>
        );
    }

    return (
        <DateDisplayDate
            value={value}
            showTime={showTime}
            data-testid={dataTestId}
            showDayOfWeek={showDayOfWeek}
            hideCurrentYear={hideCurrentYear}
        />
    );
}
